import { Component, type ErrorInfo, type ReactNode } from "react";
import ErrorFallback, { type ErrorFallbackProps } from "./ErrorFallback";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** カスタムフォールバックコンポーネント */
  fallback?: React.ComponentType<ErrorFallbackProps>;
  /** フォールバック表示のバリアント */
  variant?: "page" | "section" | "inline";
  /** エラー発生時のコールバック */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** エラーリセット時のコールバック */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary コンポーネント
 *
 * 子コンポーネントでの JavaScript エラーをキャッチし、
 * ページ全体のクラッシュを防いでフォールバック UI を表示します。
 *
 * @example
 * ```tsx
 * <ErrorBoundary variant="page">
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * // セクション単位でのエラーハンドリング
 * <ErrorBoundary variant="section">
 *   <ServiceList />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // 開発環境でのコンソールログ
    if (import.meta.env.DEV) {
      console.group("🚨 ErrorBoundary caught an error");
      console.error("Error:", error);
      console.error("Component stack:", errorInfo.componentStack);
      console.groupEnd();
    }

    // 親コンポーネントへのエラー通知
    this.props.onError?.(error, errorInfo);
  }

  resetErrorBoundary = (): void => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback: FallbackComponent = ErrorFallback, variant = "page" } = this.props;

    if (hasError && error) {
      return (
        <FallbackComponent
          error={error}
          resetErrorBoundary={this.resetErrorBoundary}
          variant={variant}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;
export { ErrorBoundary };
